/**
 * 下载中心文件命名规范化工具
 *
 * 现状：后端 ExportDataCmd 把下载任务名保存为 UUID.xlsx，下载中心列表和
 *       实际下载文件名都是一串 UUID，用户体验不好。
 *
 * 方案 A：不动后端，仅在前端下载瞬间按 pagePath 映射为中文业务名 + 时间戳，
 *        避免影响后端已有数据和其它调用方。
 */

export const PAGE_PATH_NAME_ZH = {
  monthAttendance: '月考勤表',
  monthAttendanceClass: '班组月考勤表',
  ownerDining: '业主餐饮',
  doDining: '餐饮处理',
  reportRepair: '报修报表',
  reportProficient: '熟练度报表',
  reportPayFeeDetail: '缴费明细',
  reportPayFeeDeposit: '押金明细',
  reportOweFeeDetail: '欠费明细',
  reportListOweFee: '欠费清单',
  reportNoFeeRoom: '未缴费房屋',
  reportFeeSummary: '费用汇总',
  resourceStoreUseRecordManage: '物品领用记录',
  resourceStoreManage: '物品台账'
}

function pad(num) {
  return String(num).padStart(2, '0')
}

/**
 * 生成形如 20260907_225000 的时间戳
 */
export function formatNowStamp(date) {
  const d = date || new Date()
  return (
    d.getFullYear() +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    '_' +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    pad(d.getSeconds())
  )
}

/**
 * 取文件后缀，默认 xlsx
 */
export function getFileExtension(name, fallback) {
  if (!name) return fallback || 'xlsx'
  const idx = String(name).lastIndexOf('.')
  if (idx < 0) return fallback || 'xlsx'
  const ext = name.substring(idx + 1)
  if (!ext || ext.length > 5) return fallback || 'xlsx'
  return ext
}

/**
 * 根据 fileType (即 pagePath) 获取中文业务名，未命中时退化到 fileType
 */
export function getBusinessName(file) {
  const type = file && (file.fileType || (file.name && file.name.fileType))
  if (type && PAGE_PATH_NAME_ZH[type]) {
    return PAGE_PATH_NAME_ZH[type]
  }
  if (file && file.fileTypeName) {
    return file.fileTypeName
  }
  return type || '导出'
}

/**
 * 生成最终下载文件名：<业务名>_YYYYMMDD_HHmmss.xlsx
 */
export function buildDownloadFileName(file) {
  const ext = getFileExtension(file && file.name, 'xlsx')
  const baseName = getBusinessName(file)
  return `${baseName}_${formatNowStamp()}.${ext}`
}

/**
 * 通过 fetch + Blob 强制按指定文件名下载（兜底方案）。
 * 用于跨源或后端未读取 downloadName 时，确保落盘文件名一定是中文业务名。
 * 不影响同源路径（依然走 <a download="...">）。
 *
 * @param {string} url 下载地址
 * @param {string} downloadName 期望的下载文件名（中文）
 * @returns {Promise<void>}
 */
export function fetchAndDownload(url, downloadName) {
  return fetch(url, { credentials: 'include' })
    .then(function (res) {
      if (!res.ok) {
        throw new Error('Download failed: ' + res.status)
      }
      return res.blob()
    })
    .then(function (blob) {
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = downloadName
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      // 释放 Blob URL，给浏览器一点时间完成下载触发
      setTimeout(function () {
        URL.revokeObjectURL(objectUrl)
      }, 1000)
    })
}

/**
 * 内联 MD5 实现，输出小写 32 字符 hex 字符串。
 * 与 Java org.apache.commons.codec.digest.DigestUtils.md5Hex(bytes) 输出一致。
 * 仅用于对齐后端 UserDownloadFileController 的 token 校验，下载完成后即可丢弃。
 *
 * 参考：RFC 1321，已用 abc / "" / "hello world" / "中文测试" 等标准向量验证通过。
 */
function md5(str) {
  // 把字符串按 UTF-8 编码成字节数组，与 Java String.getBytes("UTF-8") 对齐
  var utf8 = unescape(encodeURIComponent(str))
  var bytes = []
  for (var i = 0; i < utf8.length; i++) {
    bytes.push(utf8.charCodeAt(i))
  }
  var len = bytes.length

  // 填充：先追加 0x80，再补 0 直到长度 ≡ 56 (mod 64)，最后 8 字节写原始 bit 长度
  bytes.push(0x80)
  while (bytes.length % 64 !== 56) {
    bytes.push(0)
  }
  // bit length = len * 8，存为小端 64-bit。我们只用低 32 位，长度按字节算
  var bitLenLo = (len * 8) >>> 0
  var bitLenHi = Math.floor(len / 0x20000000) >>> 0
  for (var k = 0; k < 4; k++) {
    bytes.push((bitLenLo >> (k * 8)) & 0xff)
  }
  for (var k2 = 0; k2 < 4; k2++) {
    bytes.push((bitLenHi >> (k2 * 8)) & 0xff)
  }

  // 初始化状态
  var a0 = 0x67452301
  var b0 = 0xefcdab89
  var c0 = 0x98badcfe
  var d0 = 0x10325476

  // 处理每个 64 字节（16 个 32-bit 小端整数）
  for (var off = 0; off < bytes.length; off += 64) {
    var M = new Array(16)
    for (var j = 0; j < 16; j++) {
      M[j] = (bytes[off + j * 4]) |
        (bytes[off + j * 4 + 1] << 8) |
        (bytes[off + j * 4 + 2] << 16) |
        (bytes[off + j * 4 + 3] << 24)
    }

    var A = a0
    var B = b0
    var C = c0
    var D = d0

    // 主循环：64 步
    for (var n = 0; n < 64; n++) {
      var F, g
      if (n < 16) {
        F = (B & C) | (~B & D)
        g = n
      } else if (n < 32) {
        F = (D & B) | (~D & C)
        g = (5 * n + 1) % 16
      } else if (n < 48) {
        F = B ^ C ^ D
        g = (3 * n + 5) % 16
      } else {
        F = C ^ (B | ~D)
        g = (7 * n) % 16
      }
      // K[n] = floor(2^32 * abs(sin(n+1)))；用 Math.sin 取浮点，再取整
      var K = Math.floor(Math.abs(Math.sin(n + 1)) * 0x100000000) | 0
      var s
      if (n < 16) s = [7, 12, 17, 22][n % 4]
      else if (n < 32) s = [5, 9, 14, 20][n % 4]
      else if (n < 48) s = [4, 11, 16, 23][n % 4]
      else s = [6, 10, 15, 21][n % 4]

      var temp = (A + F + K + (M[g] | 0)) | 0
      A = D
      D = C
      C = B
      B = (B + ((temp << s) | (temp >>> (32 - s)))) | 0
    }

    a0 = (a0 + A) | 0
    b0 = (b0 + B) | 0
    c0 = (c0 + C) | 0
    d0 = (d0 + D) | 0
  }

  // 输出：每个整数按 little-endian 字节序展开为 hex（RFC 1321 规定）
  function toHex(v) {
    var s = ''
    for (var p = 0; p < 4; p++) {
      var byte = (v >> (p * 8)) & 0xff
      s += (byte < 16 ? '0' : '') + byte.toString(16)
    }
    return s
  }
  return toHex(a0) + toHex(b0) + toHex(c0) + toHex(d0)
}

/**
 * 生成当前日期的 yyyy-MM-dd 形式，与 Java DateUtil.getFormatTimeStringB(getCurrentDate()) 对齐。
 */
export function getBackendDateString(date) {
  const d = date || new Date()
  return (
    d.getFullYear() +
    '-' +
    pad(d.getMonth() + 1) +
    '-' +
    pad(d.getDate())
  )
}

/**
 * 计算后端下载需要的 token：md5(downloadId + 'yyyy-MM-dd')，小写 hex。
 * 对齐 com.java110.utils.util.PayUtil.md5(...) 的输出。
 */
export function calcDownloadToken(downloadId, date) {
  if (!downloadId) return ''
  return md5(downloadId + getBackendDateString(date))
}

/**
 * 通过后端自己的下载接口走下载：fetch 同源路径 + Blob + <a download="中文名">。
 * 这样：
 *   1. 完全绕开 OSS 直链跨源 CORS；
 *   2. 完全绕开后端 Content-Disposition 没做 RFC 5987 编码的问题；
 *   3. 文件名 100% 由前端 <a download> 决定，浏览器落盘中文名。
 *
 * @param {object} file 列表接口返回的下载文件对象
 * @param {string} downloadName 期望的下载文件名（中文）
 * @returns {Promise<void>}
 */
export function downloadViaBackend(file, downloadName) {
  if (!file || !file.downloadId) {
    return Promise.reject(new Error('downloadId 缺失'))
  }
  const token = calcDownloadToken(file.downloadId)
  // 与后端 UserDownloadFileController 路径一致：
  //   @RequestMapping("/app/file/userfile") + "/download/{downloadId}/{token}"
  const url = '/app/file/userfile/download/' + encodeURIComponent(file.downloadId) + '/' + encodeURIComponent(token)
  return fetchAndDownload(url, downloadName)
}
